import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Route, Routes } from "react-router-dom";

import {
  mapInsuranceVerificationFilterField,
  mapInsuranceVerificationSortField,
} from "../../api/filters/insuranceVerificationFilters";
import type QueryClient from "../../api/query/queryClient";
import { PREDEFINED_INSURANCE_VERIFICATION_FILTERS } from "../../status";
import {
  type BackendFilters,
  getPersistedBackendFilters,
} from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { isoNow, practiceSelectionKey } from "../../utils/utils";
import {
  PostedState,
  postInsuranceVerificationPMSUpdateAndNotify,
} from "../posting";
import UnpostDialog from "../UnpostDialog";
import type { InsuranceVerificationSearchParams } from "./api";
import { patchInsuranceVerification } from "./hooks";
import {
  applyVerificationUpdate,
  getDefaultSortModel,
  INSURANCE_VERIFICATION_PERSIST_KEY,
  type InsuranceVerificationStore,
  type InsuranceVerificationStoreAction,
} from "./store";
import { InsuranceVerificationDetailPage, VerificationListView } from "./views";

const mapFilterToTab = (
  filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS,
): string => {
  switch (filter) {
    case PREDEFINED_INSURANCE_VERIFICATION_FILTERS.QUEUED:
      return "QUEUED";
    case PREDEFINED_INSURANCE_VERIFICATION_FILTERS.REVIEW:
      return "REVIEW";
    case PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL:
      return "ALL";
    default:
      return "ALL";
  }
};

const getInitialSort = (filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS) => {
  const [{ field, sort }] = getDefaultSortModel(filter);
  return { field: mapInsuranceVerificationSortField(field), direction: sort };
};

function isInsuranceVerificationPostedResult(
  value: unknown,
): value is InsuranceVerificationPostedResult {
  return (
    typeof value === "object" &&
    value !== null &&
    "insuranceVerificationId" in value
  );
}

interface InsuranceVerificationDashboardProps {
  store: InsuranceVerificationStore;
  updateStore: (action: InsuranceVerificationStoreAction) => void;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  selectedPractices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
}

export default function InsuranceVerificationDashboard({
  store,
  updateStore,
  queryClient,
  updateSnackBar,
  selectedPractices,
  supportedPayers,
}: InsuranceVerificationDashboardProps) {
  const rqClient = useTanStackQueryClient();
  const [backendFilters, setBackendFilters] = useState<BackendFilters>(() =>
    getPersistedBackendFilters(
      INSURANCE_VERIFICATION_PERSIST_KEY,
      mapInsuranceVerificationFilterField,
    ),
  );
  const initialSort = getInitialSort(store.selectedFilter);
  const [sortField, setSortField] = useState<string | null>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialSort.direction,
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isDevMode = useDevMode();
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedPracticesKey stands in for selectedPractices
  const searchParams = useMemo<InsuranceVerificationSearchParams>(
    () => ({
      practiceIds: selectedPractices.map((p) => p.wieldyId),
      tab: mapFilterToTab(store.selectedFilter),
      page,
      pageSize,
      sortField,
      sortDirection,
      filters: backendFilters,
      devMode: isDevMode,
      includeAutoFiltered: store.includeAutoFiltered,
    }),
    [
      selectedPracticesKey,
      store.selectedFilter,
      page,
      pageSize,
      sortField,
      sortDirection,
      backendFilters,
      isDevMode,
      store.includeAutoFiltered,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar, rqClient would change when this runs
  const updateVerification = useCallback(
    async (payload: InsuranceVerificationUpdateMessage) => {
      try {
        await queryClient.updatePatientInsuranceVerification(payload);
        patchInsuranceVerification(rqClient, payload.wieldyId, (v) =>
          applyVerificationUpdate(v, payload),
        );
        updateSnackBar({
          severity: "success",
          message: "Insurance details updated successfully.",
        });
      } catch (_error) {
        updateSnackBar({
          severity: "error",
          message:
            "Failed to update insurance details. Please try again later.",
        });
      }
    },
    [queryClient],
  );

  const postToPMS = useCallback(
    async (verification: InsuranceVerificationMessage) => {
      await postInsuranceVerificationPMSUpdateAndNotify(
        verification,
        ({ verificationToUpdate }) =>
          patchInsuranceVerification(
            rqClient,
            verificationToUpdate.wieldyId,
            () => verificationToUpdate,
          ),
        queryClient,
        updateSnackBar,
      );
    },
    [rqClient, queryClient, updateSnackBar],
  );

  const unpost = useCallback(
    async (verification: InsuranceVerificationMessage) => {
      const result = await queryClient.resetInsuranceVerification(
        verification.wieldyId,
      );
      if (isInsuranceVerificationPostedResult(result)) {
        patchInsuranceVerification(rqClient, verification.wieldyId, () => ({
          ...verification,
          postedState: result.postedState,
          postedDateTime: result.postedDateTime,
          postedAttempts: result.postedAttempts,
          postedLatestErrorMessage: result.postedLatestErrorMessage,
          postingCompletedSteps: result.postingCompletedSteps,
          postingExpectedSteps: result.postingExpectedSteps,
        }));
        updateSnackBar({
          severity: "success",
          message: "Insurance verification unposted successfully.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: result.reason,
        });
      }
    },
    [queryClient, rqClient, updateSnackBar],
  );

  const [verificationToUnpost, setVerificationToUnpost] =
    useState<InsuranceVerificationMessage | null>(null);

  const onConfirmUnpost = async () => {
    if (verificationToUnpost) {
      await unpost(verificationToUnpost);
    }
    setVerificationToUnpost(null);
  };

  const markManuallyPosted = useCallback(
    async (verification: InsuranceVerificationMessage) => {
      try {
        await queryClient.updatePatientInsuranceVerification({
          wieldyId: verification.wieldyId,
          practiceId: verification.practiceId,
          postedState: PostedState[PostedState.MANUALLY_POSTED],
          postedDatetime: isoNow(),
        });
        patchInsuranceVerification(rqClient, verification.wieldyId, () => ({
          ...verification,
          postedState: PostedState[PostedState.MANUALLY_POSTED],
        }));
        updateSnackBar({
          severity: "success",
          message: "Insurance verification marked as manually posted",
        });
      } catch (_error) {
        updateSnackBar({
          severity: "error",
          message: "Failed to mark insurance verification as manually posted",
        });
      }
    },
    [queryClient, rqClient, updateSnackBar],
  );

  const setSelectedFilter = useCallback(
    (filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS) => {
      if (filter !== store.selectedFilter) {
        const newSort = getInitialSort(filter);
        setSortField(newSort.field);
        setSortDirection(newSort.direction);
        setPage(1);
      }
      updateStore({ type: "FILTER_CHANGED", filter });
    },
    [updateStore, store.selectedFilter],
  );

  const setIncludeAutoFiltered = useCallback(
    (includeAutoFiltered: boolean) => {
      updateStore({ type: "INCLUDE_AUTO_FILTERED", includeAutoFiltered });
    },
    [updateStore],
  );

  const handleBackendFilterChange = useCallback(
    (filters: BackendFilters) => {
      if (JSON.stringify(filters) === JSON.stringify(backendFilters)) return;
      setBackendFilters(filters);
      setPage(1);
    },
    [backendFilters],
  );

  const handleSortChange = useCallback(
    (field: string, direction: "asc" | "desc") => {
      setSortField(field);
      setSortDirection(direction);
    },
    [],
  );

  const handlePageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      setPage(newPage);
      setPageSize(newPageSize);
    },
    [],
  );

  return (
    <>
      <UnpostDialog
        open={!!verificationToUnpost}
        title="Unpost verification"
        description="Before unposting, open this patient's record in the PMS and confirm the verification is not there. Unposting clears what Wieldy recorded, and the next post enters the verification again."
        confirmTestId="confirmUnpostVerification"
        onClose={() => setVerificationToUnpost(null)}
        onConfirm={onConfirmUnpost}
      />
      <Routes>
        <Route
          path="/"
          element={
            <VerificationListView
              searchParams={searchParams}
              queryClient={queryClient}
              updateSnackBar={updateSnackBar}
              updateVerification={updateVerification}
              postToPMS={postToPMS}
              unpost={setVerificationToUnpost}
              markManuallyPosted={markManuallyPosted}
              selectedFilter={store.selectedFilter}
              setSelectedFilter={setSelectedFilter}
              selectedPractices={selectedPractices}
              includeAutoFiltered={store.includeAutoFiltered}
              setIncludeAutoFiltered={setIncludeAutoFiltered}
              onFilterChange={handleBackendFilterChange}
              onSortChange={handleSortChange}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          }
        />
        <Route
          path="/:verificationId"
          element={
            <InsuranceVerificationDetailPage
              store={store}
              updateStore={updateStore}
              queryClient={queryClient}
              updateSnackBar={updateSnackBar}
              supportedPayers={supportedPayers}
            />
          }
        />
      </Routes>
    </>
  );
}
